import { useEffect, useMemo, useState } from "react";
import {
  fetchAdminPendingStores,
  reviewPendingStore,
} from "../../../api/storeRequest.js";
import dayjs from "dayjs";
import jalaliday from "jalaliday";
import Swal from "sweetalert2";
import "../../../styles/AdminPanel/AdminPendingStores.css";
import {
  CHECK_ICON,
  Icon,
  X_ICON,
} from "../../panel/user/manageShop/shared/EditShopShared.jsx";
import {
  swalConfirm,
  swalError,
  swalReject,
  swalSuccess,
  swalWarning,
} from "../../../utils/swalAlert.js";

dayjs.extend(jalaliday);

const STATUS_LABEL = {
  pending: "در انتظار",
  approved: "تایید شده",
  rejected: "رد شده",
};
const DocumentIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="rgba(43, 18, 76, 0.55)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </svg>
);
const PAGE_SIZE_OPTIONS = [8, 16, 24];

const toFa = (n) => Number(n || 0).toLocaleString("fa-IR");

// آیکون تیک برای دکمه‌ی تایید (SVG داخلی، بدون نیاز به bootstrap icons)
const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// آیکون ضربدر برای دکمه‌ی رد
const XIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function AdminPendingStores() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAdminPendingStores()
      .then((res) => setList(res.data))
      .catch(() => setError("خطا در بارگذاری درخواست‌ها"))
      .finally(() => setLoading(false));
  }, []);

  const totalCount = list.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // اگر بعد از تغییر pageSize یا حذف موارد، صفحه فعلی از تعداد کل صفحات بیشتر شد
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, page, pageSize]);

  const handlePageSize = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleReview = async (pk, action) => {
    if (action === "approve") {
      const result = await swalConfirm({
        title: "تایید درخواست",
        text: "آیا از تایید این درخواست مطمئن هستید؟",
      });

      if (!result) return;

      try {
        await reviewPendingStore(pk, action, "");
        setList((prev) =>
          prev.map((s) =>
            s.id === pk ? { ...s, status: "approved", admin_note: "" } : s,
          ),
        );

        swalSuccess({ title: "تایید شد", text: "فروشگاه با موفقیت تایید شد." });
      } catch (err) {
        swalError({
          title: "خطا",
          text: "خطا در ثبت عملیات",
        });
        console.error(err);
      }
    } else {
      const { note, isConfirmed } = await swalReject({});

      if (!isConfirmed) return;

      try {
        await reviewPendingStore(pk, action, note);
        setList((prev) =>
          prev.map((s) =>
            s.id === pk ? { ...s, status: "rejected", admin_note: note } : s,
          ),
        );

        swalWarning({ title: "رد شد", text: "درخواست با موفقیت رد شد." });
      } catch (err) {
        swalError({ title: "خطا", text: "خطا در ثبت عملیات" });
        console.error(err);
      }
    }
  };

  const showAdminNote = (note) => {
    console.log(note);
    swalWarning({
      title: "یادداشت ادمین",
      text: note || "یادداشتی ثبت نشده",
    });
  };

  if (loading)
    return (
      <div className="admin-pending">
        <div className="admin-pending-loading">در حال بارگذاری...</div>
      </div>
    );
  if (error)
    return (
      <div className="admin-pending">
        <div className="admin-pending-error">{error}</div>
      </div>
    );

  return (
    <div className="admin-pending">
      <div className="admin-pending-table-card">
        <div className="admin-pending-table-wrapper">
          <table className="admin-pending-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>نام فروشگاه</th>
                <th>مالک</th>
                <th>سازنده</th>
                <th>نقش سازنده</th>
                <th>پلن</th>
                <th>شماره پیگیری</th>
                <th>مبلغ پرداخت شده</th>
                <th>تخفیف</th>
                <th>وضعیت</th>
                <th>تاریخ ثبت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr className="admin-pending-empty">
                  <td colSpan="10">هیچ درخواستی یافت نشد.</td>
                </tr>
              )}
              {paginatedList.map((s) => (
                <tr key={s.id}>
                  <td data-label="ID">{s.id}</td>
                  <td data-label="نام فروشگاه">{s.store_name}</td>
                  <td data-label="مالک">{s.owner_name ?? "—"}</td>
                  <td data-label="سازنده">{s.creator_name ?? "—"}</td>
                  <td data-label="نقش سازنده">
                    <span
                      className={`admin-pending-role-badge ${
                        s.creator_role === "marketer"
                          ? "admin-pending-role-badge--marketer"
                          : "admin-pending-role-badge--user"
                      }`}
                    >
                      {s.creator_role === "marketer" ? "مارکتر" : "کاربر"}
                    </span>
                  </td>
                  <td data-label="پلن">{s.plan_title ?? "—"}</td>
                  <td data-label="شماره پیگیری">
                    {s.transaction_number ?? "—"}
                  </td>
                  <td data-label="مبلغ پرداخت شده">
                    {Number(s.payment_price).toLocaleString("fa-IR")}
                  </td>

                  <td data-label="تخفیف">{s.discount ?? "—"}</td>
                  <td data-label="وضعیت">
                    <span
                      className={`admin-pending-status admin-pending-status--${s.status}`}
                    >
                      {STATUS_LABEL[s.status] ?? s.status}
                    </span>
                  </td>
                  <td data-label="تاریخ ثبت">
                    {dayjs(s.created_at)
                      .calendar("jalali")
                      .format("YY/MM/DD HH:mm")}
                  </td>
                  <td data-label="عملیات">
                    {s.status === "pending" ? (
                      <div className="admin-pending-actions">
                        <button
                          className="esp-icon-btn esp-icon-btn--check"
                          onClick={() => handleReview(s.id, "approve")}
                          aria-label="تایید"
                          title="تایید"
                        >
                          <Icon>{CHECK_ICON}</Icon>
                        </button>
                        <button
                          className="esp-icon-btn esp-icon-btn--remove"
                          onClick={() => handleReview(s.id, "reject")}
                          aria-label="رد"
                          title="رد"
                        >
                          <Icon>{X_ICON}</Icon>
                        </button>
                      </div>
                    ) : (
                      <div className="admin-pending-final">
                        <span className="admin-pending-final-label">
                          {s.status === "approved" ? "تایید شده" : "رد شده"}
                        </span>
                        {s.status === "rejected" && (
                          <button
                            title="مشاهده یادداشت"
                            className="admin-pending-note-btn"
                            onClick={() => showAdminNote(s.admin_note)}
                          >
                            <DocumentIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ---- صفحه‌بندی ---- */}
        {totalCount > 0 && (
          <div className="admin-pending-pagination">
            <div className="admin-pending-pagination-info-group">
              <span className="admin-pending-pagination-info">
                {toFa(totalCount)} درخواست — صفحه {toFa(page)} از{" "}
                {toFa(totalPages)}
              </span>
              <select
                className="admin-pending-page-size-select"
                value={pageSize}
                onChange={(e) => handlePageSize(e.target.value)}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {toFa(n)}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-pending-pagination-controls">
              <button
                className="admin-pending-page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`admin-pending-page-btn number${n === page ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {toFa(n)}
                </button>
              ))}
              <button
                className="admin-pending-page-btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
